/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import PluginCard from "../ui/PluginCard";
import Icon from "../ui/Icon";
import Text from "../ui/Text";
import Button from "../ui/Button";
import { useTranslate } from "../../hooks/useTranslate";
import { useMembershipCheckout } from "../../hooks/checkout/useMembershipCheckout";
import { formatMembershipDuration } from "../../utils/helpers/membership";
import { format } from "date-fns";
import StartDatePicker from "../ui/form/StartDatePicker";
import FormatDateTime from "../formatters/FormatDateTime";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import FormatPrice from "../formatters/FormatPrice";
import styled from "styled-components";

const CartControls = styled(Stack)`
  width: 100%;
`;

const MembershipItem = React.memo(({
  membership,
  highlightServiceId = null,
  disabled,
  reserveDescription = false,
  isPurchased = false,
}) => {
  const { _t } = useTranslate();
  const { checkout, hasActiveRecurringProfile } = useMembershipCheckout();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const isDisabled = disabled || hasActiveRecurringProfile(membership.id);
  const durationLabel = formatMembershipDuration(membership.duration, membership.duration_type, _t);
  const sessionsValue = membership.is_unlimited ? _t("Unlimited") : membership.limit;
  const sessionsLabel = `${_t("sessions")}: ${sessionsValue}`;
  const servicesInfo = membership.services_info || [];
  const servicesCount = servicesInfo.length;

  const highlightedService = highlightServiceId
    ? servicesInfo.find((s) => +s.id === +highlightServiceId)
    : null;

  const otherServicesNames = highlightedService
    ? servicesInfo.filter((s) => +s.id !== +highlightServiceId).map((s) => s.name).join(", ")
    : null;

  const servicesNamesNode = highlightedService ? (
    <>
      <Text as="span" weight="bold" color="accent" size="small">{highlightedService.name}</Text>
      {otherServicesNames && `, ${otherServicesNames}`}
    </>
  ) : (
    servicesInfo.map((s) => s.name).join(", ")
  );

  const includes = [
    servicesCount > 0 && {
      icon: <Icon.CardsStack />,
      label: _t("Attached services: %count%", { count: servicesCount }),
      name: servicesNamesNode,
    },
    { icon: <Icon.CalendarDetailed />, name: durationLabel },
    { icon: <Icon.FileList />, name: sessionsLabel },
  ].filter(Boolean);

  const handleBuy = () => {
    const startDate = membership.fixed_start_date || format(selectedDate, "yyyy-MM-dd");
    checkout({ membership, sourceServiceId: highlightServiceId, startDate });
  };

  const startDateSection = membership.fixed_start_date ? (
    <Flex $gap="0.25rem" $align="baseline" $wrap="wrap">
      <Text size="small">Starts from</Text>
      <Text size="small" color="dark">
        <FormatDateTime value={membership.fixed_start_date} format={FormatDateTime.FORMAT.DATE} />
      </Text>
    </Flex>
  ) : (
    <StartDatePicker
      date={selectedDate}
      onChange={setSelectedDate}
      minDate={new Date()}
    />
  );

  const cartControls = (
    <CartControls $gap="0.5rem" $align="center">
      <Button title="Buy" onClick={handleBuy} variant="outline" fullWidth disabled={isDisabled} />
      {membership.is_recurring && membership.recurring_price && (
        <Text size="small">
          {_t("Renewal")} <FormatPrice value={membership.recurring_price} currency={membership.currency} />
        </Text>
      )}
    </CartControls>
  );

  const modalFooter = (
    <Stack $gap="0.5rem" $align="center">
      {startDateSection}
      {cartControls}
    </Stack>
  );

  return (
    <PluginCard
      disabled={disabled}
      badge={isPurchased ? "Purchased" : undefined}
      name={membership.name}
      price={membership.first_price}
      currency={membership.currency}
      description={membership.description}
      reserveDescription={reserveDescription}
      picture={membership.picture_preview}
      fallbackIcon={<Icon.MembershipItem size={87} color="iconStroke" strokeWidth={0.5} />}
      includes={includes}
      includeLimit={3}
      beforeFooter={startDateSection}
      footer={cartControls}
      modalFooter={modalFooter}
      closeOnSelect
    />
  );
});

export default MembershipItem;
